import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";

interface Profit {
  id: string;
  date: string;
  profit: string;
  profit_without_currency: string;
}
interface ProfitWithFooter extends Profit {
  isFooter?: boolean;
}
export default function ProfitReport() {
  const [filteredItems, setFilteredItems] = useState<Profit[]>([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const { currency } = useCurrency();
  // console.log(currency);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const formatDate = (date: Date | null): string | null => {
    if (!date) return null;

    // Ensure local YYYY-MM-DD format
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2); // Month is 0-indexed
    const day = `0${date.getDate()}`.slice(-2);

    return `${year}-${month}-${day}`;
  };

  const [sortColumn, setSortColumn] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchData = async (page = 1, perPage = 10, sortField = sortColumn, sortDir = sortOrder) => {
    setLoading(true);
    try {
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);

      const response = await axios.get(
        `${baseUrl}call_history/getDailyFinancialReport`,
        {
          params: {
            page,
            per_page: perPage,
            from_date: formattedFromDate,
            to_date: formattedToDate,
            sort_by: sortField,
            sort_order: sortDir,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data: Profit[] = response.data.data;
      setFilteredItems(data);
      setTotalRows(response.data.total);
      setCurrentPage(response.data.current_page);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, fromDate, toDate]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage, sortColumn, sortOrder);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchData(1, newPerPage, sortColumn, sortOrder);
  };

  const handleSort = (column: any, sortDirection: string) => {
    const sortField = column.sortField || "date";

    setSortColumn(sortField);
    setSortOrder(sortDirection);
    setCurrentPage(1);
    fetchData(1, perPage, sortField, sortDirection);
  };

  const handleFullReset = () => {
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
  };

  const formatProfit = (profit: string) => {
    const number = parseFloat(profit.replace(/[^0-9.-]+/g, ""));
    const absProfit = Math.abs(number).toLocaleString();

    return number < 0 ? `-${absProfit}` : `${absProfit}`;
  };

  const getProfitColumns = () => {
    return [
      {
        name: <div style={{ whiteSpace: "normal" }}>S.No</div>,
        cell: (row: ProfitWithFooter, index: number) =>
          row.isFooter ? "" : (currentPage - 1) * perPage + index + 1,
        width: "80px",
      },
      {
        name: <div style={{ whiteSpace: "normal" }}>Date</div>,
        selector: (row: Profit) => row.date,
        sortable: true,
        sortField: "date",
        cell: (row: ProfitWithFooter) =>
          row.isFooter ? <strong>Total</strong> : row.date,
      },
      {
        name: (
          <div style={{ whiteSpace: "normal" }}>
            Profit {currency ? `(${currency})` : ""}
          </div>
        ),
        selector: (row: Profit) => row.profit,
        sortable: true,
        sortField: "profit",
        cell: (row: ProfitWithFooter) => {
          if (row.isFooter) {
            return (
              <strong style={{ color: totalAmount < 0 ? "red" : "green" }}>
                {currency}{" "}
                {totalAmount < 0
                  ? `-${Math.abs(totalAmount).toLocaleString()}`
                  : `${totalAmount.toLocaleString()}`}
              </strong>
            );
          } else {
            const value = parseFloat(row.profit_without_currency);
            const formatted = formatProfit(row.profit);
            return (
              <span style={{ color: value < 0 ? "red" : "green" }}>
                {formatted}
              </span>
            );
          }
        },
      },
    ];
  };

  const calculateTotals = () => {
    const totalAmount = filteredItems.reduce((acc, row) => {
      const amount = parseFloat(
        (row.profit_without_currency || "0").replace(/,/g, "")
      );
      return acc + amount;
    }, 0);

    return {
      totalAmount,
    };
  };
  const { totalAmount } = calculateTotals();

  const columnsData: ProfitWithFooter[] = [
    ...filteredItems,
    {
      isFooter: true,
      id: "",
      date: "Total",
      profit: "",
      profit_without_currency: "",
    },
  ];

  const fetchAllDataWithoutPagination = async () => {
    try {
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);
      const response = await axios.get(
        `${baseUrl}call_history/getDailyFinancialReportNoPagination`,
        {
          params: {
            from_date: formattedFromDate,
            to_date: formattedToDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      //   console.log(response.data, "data"); // Use response.data directly
      return response.data; // Return the response directly
    } catch (error) {
      console.error("Error fetching all data without pagination:", error);
      return []; // Return an empty array on error
    }
  };

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row: any) => row.isFooter === true,
      style: {
        fontWeight: "800",
        backgroundColor: "#f8f9fa",
        color: "#308e87",
        borderTop: "2px solid #dee2e6",
        fontSize: "16px",
      },
    },
  ];

  const exportPDF = async () => {
    const allData = await fetchAllDataWithoutPagination();
    const tableData = allData.map((row: any, index: number) => [
      index + 1,
      row.date || "N/A",
      row.profit_without_currency || "0",
    ]);

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const pdfCurrency = currencyMap[currency] || currency;

    const footerRow = ["", "Total", `${totalAmount}`];

    exportToPDF({
      fileName: "Profit_Report",
      columns: ["S.No", "Date", `Profit ${pdfCurrency ? `(${pdfCurrency})` : ""}`],
      data: tableData,
      emptyDataMessage: "No profit data available for export.",
      footerRow,
      autoTableOptions: {
        headStyles: { fillColor: [0, 105, 92] },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { halign: "center" },
          2: { halign: "right" },
        },
        didParseCell: (data: any) => {
          if (data.row.index === tableData.length) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [245, 245, 245];
          }
          if (data.column.index === 2 && data.cell.section === "body") {
            const val = parseFloat(data.cell.raw);
            if (val < 0) data.cell.styles.textColor = [211, 47, 47];
            else if (val > 0) data.cell.styles.textColor = [0, 105, 92];
          }
        },
      },
    });
  };

  const exportExcel = async () => {
    const allData = await fetchAllDataWithoutPagination();
    if (!allData.length) {
      toast.warn("No profit data available for export.", {
        position: "top-right",
        autoClose: 1000,
      });
      return;
    }

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const excelCurrency = currencyMap[currency] || currency;
    const profitHeader = `Profit ${excelCurrency ? `(${excelCurrency})` : ""}`;

    const tableData = allData.map((row: Profit, index: number) => {
      return {
        "S.No": index + 1,
        Date: row.date || "N/A",
        [profitHeader]: row.profit_without_currency || "0",
      };
    });

    const footerRow = {
      "S.No": "",
      Date: "Total",
      [profitHeader]: totalAmount,
    };

    excelExportUtils(tableData, "Profit_Report", "Profit Report", footerRow);
  };

  return (
    <>
      <Breadcrumbs
        pageTitle="Cash Flow Report"
        parent="Reports"
        title="Cash Flow Report"
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-end">
              <Col xl={2}>
                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    onClick={exportPDF}
                    className="d-flex justify-content-center  p-0"
                    style={{ width: "30px", height: "30px" }}
                    title="PDF"
                  >
                    <FaFilePdf size={16} />
                  </Button>
                  <Button
                    color="secondary"
                    onClick={exportExcel}
                    className="d-flex justify-content-center  p-0"
                    style={{ width: "30px", height: "30px" }}
                    title="EXCEL"
                  >
                    <FaFileExcel size={16} />
                  </Button>
                </div>
              </Col>

              <Col xl={5}></Col>

              <Col xl={5}>
                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0">From:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={fromDate}
                      onChange={(date: Date | null) => {
                        setFromDate(date);
                        setCurrentPage(1);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Start date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  </div>

                  <label className="mb-0">To:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={toDate}
                      onChange={(date: Date | null) => {
                        setToDate(date);
                        setCurrentPage(1);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="End date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <div className="table-responsive custom-scrollbar mt-3">
              <DataTable
                data={columnsData}
                columns={getProfitColumns()}
                highlightOnHover
                striped
                customStyles={customStyles}
                pagination
                paginationServer
                paginationRowsPerPageOptions={[10, 15, 20, 30, 50, 100]}
                paginationPerPage={perPage}
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationDefaultPage={currentPage}
                sortServer
                onSort={handleSort}
                responsive
                progressPending={loading}
                progressComponent={
                  <div className="p-5">
                    <Spinner color="primary" />
                  </div>
                }
                conditionalRowStyles={conditionalRowStyles}
              />
            </div>
          </CardBody>
        </Card>
      </Col>

      <ToastContainer />
    </>
  );
}
